import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createJobForFirmAsUser,
  type FilingType,
  type JobResponse,
  useListJobsForFirmAsUser,
} from "@taxassist/api-client";
import { useMemo } from "react";
import { useCurrentFirmId } from "../contexts/FirmContext";
import {
  countJobsByUserFriendlyStatus,
  convertToRawStatuses,
  type UserFriendlyStatus,
} from "../utils/statusMapping";

export const useJobs = (options: { refetchInterval?: number } = {}) => {
  const firmId = useCurrentFirmId();

  const result = useListJobsForFirmAsUser(
    firmId as string,
    {},
    {
      query: {
        queryKey: ["jobs", firmId],
        enabled: !!firmId,
        ...options,
      },
    },
  );

  return {
    ...result,
    data: result.data?.jobs || ([] as JobResponse[]),
  };
};

export const useJobMetrics = () => {
  const { data: jobs = [] } = useJobs();
  const statusCounts = useMemo(
    () => countJobsByUserFriendlyStatus(jobs),
    [jobs],
  );

  return {
    statusCounts,
    totalJobs: jobs.length,
  };
};

export const useRecentJobs = (count: number) => {
  const { data: jobs = [] } = useJobs();
  const recentJobs = useMemo(() => {
    return [...jobs]
      .sort(
        (a, b) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
      )
      .slice(0, count);
  }, [jobs, count]);

  return { jobs: recentJobs };
};

export const useCreateJob = () => {
  const queryClient = useQueryClient();
  const firmId = useCurrentFirmId();

  return useMutation({
    mutationFn: (jobData: {
      clientName: string;
      taxYear: number;
      clientNumber: string;
      filingType: FilingType;
    }) =>
      createJobForFirmAsUser(firmId as string, {
        ...jobData,
        firmId: firmId as string,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["jobs", firmId] });
    },
  });
};

export const useFilteredJobs = (filters: {
  search?: string;
  userFriendlyStatuses?: UserFriendlyStatus[];
  filingTypes?: FilingType[];
  dateRange?: {
    start: Date | null;
    end: Date | null;
  };
}) => {
  const { data: jobs = [], ...rest } = useJobs();

  const filteredJobs = useMemo(() => {
    return jobs.filter((job) => {
      // Status filter - convert user-friendly statuses to raw statuses
      const statusMatch = (() => {
        if (
          !filters.userFriendlyStatuses ||
          filters.userFriendlyStatuses.length === 0
        ) {
          return true;
        }
        const rawStatuses = convertToRawStatuses(filters.userFriendlyStatuses);
        return rawStatuses.includes(job.status);
      })();

      // Filing type filter
      const filingTypeMatch =
        !filters.filingTypes ||
        filters.filingTypes.length === 0 ||
        filters.filingTypes.includes(job.filingType);

      // Search filter
      const searchMatch =
        !filters.search ||
        job.clientName.toLowerCase().includes(filters.search.toLowerCase()) ||
        job.jobId.toLowerCase().includes(filters.search.toLowerCase());

      // Date range filter
      const dateRangeMatch = (() => {
        if (
          !filters.dateRange ||
          (!filters.dateRange.start && !filters.dateRange.end)
        ) {
          return true;
        }
        const jobDate = new Date(job.createdAt);
        if (filters.dateRange.start && jobDate < filters.dateRange.start) {
          return false;
        }
        if (filters.dateRange.end && jobDate > filters.dateRange.end) {
          return false;
        }
        return true;
      })();

      return statusMatch && filingTypeMatch && searchMatch && dateRangeMatch;
    });
  }, [jobs, filters]);

  return { jobs: filteredJobs, ...rest };
};

export const useJobsByPriority = () => {
  const { data: jobs = [] } = useJobs();

  const jobsByPriority = useMemo(() => {
    return [...jobs].sort((a, b) => {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }, [jobs]);

  return { jobs: jobsByPriority };
};
